import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';
import ApexHeroLogo from '@/components/landing/ApexHeroLogo';
import ProofChainVisual from '@/components/landing/ProofChainVisual';
import AccessRequestModal, { useAccessGate } from '@/components/landing/AccessRequestModal';
import GovernanceContinuum from '@/components/landing/GovernanceContinuum';
import GovernanceReplay from '@/components/landing/GovernanceReplay';
import { TRUST_URL } from '@/components/apex/Sidebar';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

const CSS_ROWS = [
  ['Policy Version',          'v2.1-ldrs',          '⚠ Modified 2 hours prior',         'warn'],
  ['Data Source Allowlist',   'v4.1',               '⚠ External Legal Corpus NOT listed', 'warn'],
  ['Model Approval State',    'GPT-4o 2025-11',     '✓ Approved',                       'ok'],
  ['Evidence Coverage',       '71%',                '⚠ Below 80% threshold',            'warn'],
  ['Active Exceptions',       'None',               '⚠ No exception approved',          'warn'],
];

const FORCING = [
  ['EU AI Act · Aug 2, 2026', 'Articles 12 + 14 require audit trail, oversight, and documentation for high-risk AI.', true],
  ['SOC 2 AI Controls',       'Evidence scrutiny active now. Auditors probing existing TSC lenses (PI1.1, PI1.2, CC6.1).', false],
  ['Cyber Insurance',         'Governance documentation requirements tightening in underwriting questionnaires.', false],
  ['NIST AI RMF',             'Federal + public-sector procurement increasingly referencing the framework.', false],
];

const DEPLOY = [
  ['Aurora PostgreSQL',          'Bitemporal evidence database'],
  ['S3 Object Lock',             'Immutable payload vault · 7-year retention'],
  ['ECS/Fargate',                'Containerized trust services'],
  ['Control State Compiler',     'Signed CSS snapshot at every governance change'],
  ['Chain Integrity Engine',     'Validates proof continuity automatically'],
  ['First proof chain',          'Under 2 hours from SDK installation'],
];

export default function ProductTrust() {
  const gateOpen = useAccessGate();
  const [open, setOpen] = useState(false);

  const requestTrust = useCallback(() => {
    if (gateOpen) { window.open(TRUST_URL, '_blank', 'noopener,noreferrer'); return; }
    setOpen(true);
  }, [gateOpen]);

  return (
    <div style={{ background: '#06070A', minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="APEX Trust — Temporal AI Governance Proof"
        description="Proves what governed every AI decision — and reconstructs the governance state that existed at that exact moment. Designed for the audits and investigations that are coming."
        path="/product/trust"
      />
      <LandingNav />

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '120px 24px 64px' }}>
        <motion.div {...fadeUp(0)}>
          <div style={{ marginBottom: 28 }} aria-hidden>
            <ApexHeroLogo width={200} variant="trust" showWordmark={false} showTagline={false} />
          </div>
          <h1 style={{ fontSize: 'clamp(48px, 8vw, 80px)', fontWeight: 800, letterSpacing: '-0.03em', color: '#6366F1', margin: 0 }}>APEX TRUST</h1>
          <p style={{ fontSize: 13, color: '#6366F1', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.18em', marginTop: 8 }}>
            Temporal AI Governance Proof Infrastructure
          </p>
          <p style={{ fontSize: 18, color: '#94A3B8', maxWidth: 720, marginTop: 20, lineHeight: 1.6 }}>
            What was your AI system classified as? Which controls governed it at decision time?
            What did it decide, and how was it reviewed?
          </p>
          <p style={{ fontSize: 22, color: '#F8FAFC', marginTop: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Prove it. At any point in time.
          </p>
          <p style={{
            fontSize: 13,
            color: '#475569',
            fontFamily: 'JetBrains Mono, monospace',
            marginTop: 12,
            fontStyle: 'italic',
          }} data-testid="trust-urgency-line">
            Governance systems built for static infrastructure are breaking under AI.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <button onClick={requestTrust} style={{ background: '#6366F1', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer' }} data-testid="trust-request-hero">
              Request access <ExternalLink size={14} />
            </button>
            <Link to="/contact" style={{ border: '1px solid #6366F1', color: '#818CF8', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
              Become a design partner →
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Proof chain */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 56px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24 }}>
          The Proof Chain
        </motion.h2>
        <ProofChainVisual publicMode={true} />
        <p style={{ fontSize: 12, color: '#475569', marginTop: 24, lineHeight: 1.7, maxWidth: 720, fontFamily: 'JetBrains Mono, monospace' }}>
          Five layers. One signed chain. Designed for the audit that is coming.
        </p>
      </section>

      {/* Governance Replay — the cinematic moment */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 80px' }}>
        <motion.div {...fadeUp(0)} style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>
            Governance Replay
          </h2>
          <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 8, maxWidth: 560, lineHeight: 1.7 }}>
            Select a point in time. Watch what changes.
          </p>
          <p style={{ color: '#475569', fontSize: 11, marginTop: 6, fontFamily: 'JetBrains Mono, monospace', fontStyle: 'italic' }}>
            This is what "temporal governance" means in practice.
          </p>
        </motion.div>
        <motion.div {...fadeUp(0.08)}>
          <GovernanceReplay />
        </motion.div>
      </section>

      {/* The Moat */}
      <section style={{ background: '#0F1629', borderTop: '1px solid #1E2A45', borderBottom: '1px solid #1E2A45', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <motion.div {...fadeUp(0)}>
            <h2 style={{ fontSize: 11, color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 20 }}>
              The Moat: Control State at Decision Time
            </h2>
            <p style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.4, letterSpacing: '-0.01em', maxWidth: 760 }}>
              A decision only has governance meaning relative to the exact control state active when it occurred.
            </p>
            <div style={{ marginTop: 24, color: '#94A3B8', fontSize: 14, lineHeight: 1.8, maxWidth: 720 }}>
              <p>Most tools know your current control state.</p>
              <p>No tool stores what your control state was at 11:14 UTC on April 15th — at the moment of each AI decision.</p>
              <p style={{ color: '#F8FAFC', marginTop: 12 }}>Apex Trust does. For every governed AI decision. Automatically.</p>
            </div>
          </motion.div>

          {/* CSS panel */}
          <motion.div {...fadeUp(0.15)} style={{
            marginTop: 36, background: '#080D1A',
            border: '1px solid #1E2A45', borderLeft: '3px solid #F59E0B',
            borderRadius: 12, padding: 22, fontFamily: 'JetBrains Mono, monospace',
          }}>
            <div style={{ fontSize: 10, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 16 }}>
              Control State at Decision Time · April 15, 2026 · 11:14 UTC · ⚠ OPERATIONS-GRADE
            </div>
            {CSS_ROWS.map(([k, v, status, kind]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: 8, padding: '8px 0', borderTop: '1px solid #1E2A45', fontSize: 11 }}>
                <span style={{ color: '#475569' }}>{k}:</span>
                <span style={{ color: '#F8FAFC' }}>{v}</span>
                <span style={{ color: kind === 'warn' ? '#F59E0B' : '#10B981', textAlign: 'right' }}>{status}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #1E2A45', fontSize: 12 }}>
              <span style={{ color: '#475569' }}>Governance assessment: </span>
              <span style={{ color: '#F59E0B', fontWeight: 600 }}>⚠ CONTROL GAP · 143 decisions affected</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Continuum separator between Moat and Impossible Query — degraded state, Trust cadence */}
      <div style={{ marginTop: 0, marginBottom: 0 }}>
        <GovernanceContinuum height={120} opacity={0.28} variant="trust" degraded={true} showLabels={false} />
      </div>

      {/* The Impossible Query */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '80px 24px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 16 }}>
          Governance Intelligence
        </motion.h2>
        <motion.p {...fadeUp(0.05)} style={{ fontSize: 18, color: '#F8FAFC', maxWidth: 720, lineHeight: 1.6 }}>
          Other platforms let you filter records.<br />
          <span style={{ color: '#818CF8' }}>Apex Trust lets you query governance state.</span>
        </motion.p>

        <motion.div {...fadeUp(0.1)} style={{
          marginTop: 24, background: '#080D1A',
          border: '1px solid #1E2A45', borderRadius: 12,
          padding: 18, fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: '#94A3B8', lineHeight: 1.7,
        }}>
          <span style={{ color: '#475569', marginRight: 8 }}>{'>'}</span>
          Show every HIGH-RISK decision executed while governance state was degraded and oversight evidence was below defensibility threshold between Apr 10–15
        </motion.div>

        <motion.div {...fadeUp(0.15)} style={{
          marginTop: 18, background: '#0F1629',
          border: '1px solid #1E2A45', borderLeft: '3px solid #6366F1',
          borderRadius: 12, padding: 22,
        }}>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#F8FAFC' }}>
            <span style={{ color: '#10B981' }}>3 records matched</span> · Compound governance failure
          </div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8', marginTop: 10, lineHeight: 1.7 }}>
            Dimensions: 3 simultaneously reconstructed<br />
            Evidence coverage + Control State + Oversight signals at each decision timestamp
          </div>
          <p style={{ color: '#818CF8', fontSize: 13, marginTop: 16, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
            Proof-grade governance intelligence. Not monitoring. Reconstruction.
          </p>
          <p style={{ color: '#475569', fontSize: 11, marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
            No other governance tool performs multi-dimensional temporal reconstruction.
          </p>
        </motion.div>
      </section>

      {/* Four Forcing Functions */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 16 }}>
          Four Forcing Functions
        </motion.h2>
        <motion.p {...fadeUp(0.05)} style={{
          color: '#64748B',
          fontSize: 13,
          fontStyle: 'italic',
          marginBottom: 24,
          maxWidth: 580,
          lineHeight: 1.7,
        }}>
          Modern AI systems change faster than the governance models built to explain them.
          These four forces make historical AI governance proof operational rather than optional.
        </motion.p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          {FORCING.map(([label, body, accent], i) => (
            <motion.div key={label} {...fadeUp(0.1 * i)} style={{
              background: '#0F1629', border: '1px solid #1E2A45', borderRadius: 12, padding: 20,
            }}>
              <div style={{ fontSize: 10, color: accent ? '#6366F1' : '#475569', textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'JetBrains Mono, monospace' }}>
                {label}
              </div>
              <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 10, lineHeight: 1.6 }}>{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Deployment Facts */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px 80px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 12 }}>
          Deployment Facts
        </motion.h2>
        <motion.p {...fadeUp(0.05)} style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 12,
          color: '#6366F1',
          marginBottom: 20,
          marginTop: 0,
          letterSpacing: '0.06em',
        }}>
          Managed like SaaS. Deployed in your environment.
        </motion.p>
        <div>
          {DEPLOY.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderTop: '1px solid #1E2A45', padding: '14px 0' }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#94A3B8' }}>{k}</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#6366F1', textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + design partner line */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 80px' }}>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={requestTrust} style={{ background: '#6366F1', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer' }} data-testid="trust-request-bottom">
            Request access <ExternalLink size={14} />
          </button>
          <Link to="/contact" style={{ border: '1px solid #6366F1', color: '#818CF8', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            Become a design partner →
          </Link>
          <Link to="/platform" style={{ border: '1px solid #1E2A45', color: '#94A3B8', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
            Platform architecture →
          </Link>
        </div>
        <p style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 36, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>
          One workflow. One SDK wrapper. One proof chain. Seven days.
        </p>
      </section>

      <AccessRequestModal
        open={open}
        product="Apex Trust"
        onClose={() => setOpen(false)}
        onGranted={() => { setOpen(false); window.open(TRUST_URL, '_blank', 'noopener,noreferrer'); }}
      />
      <LandingFooter />
    </div>
  );
}
